/**
 * Alerts (website roadmap S34 — Torn Intel Platinum's price alerts and phone app, weav3r's favorites): the player's own
 * alert rules and the pure checks the server runs on them.
 *
 * - The rules are the player's own list, kept with their lists (lists sync: `/api/me/lists`, the extension's
 *   core/list-sync.ts SYNCED_LISTS) under ALERT_RULES_LIST — no new kind of stored data.
 * - The server checks them every minute (apps/api/src/alerts.ts) on what it already has: stock prices it reads with its
 *   own key (stock-history.ts), the market pool and the player's favorites with their target prices (S31/S32), stock
 *   abroad pooled from users' views and its restock model (S10), and the player's own data that background tracking
 *   already reads with their key (the bank investment's end from the hourly snapshot; the flight from their faction
 *   share). No new Torn calls, never torn.com, never page data.
 * - Delivery, per rule: the extension (a notification), the Discord bot (a DM to the player's own linked Discord only,
 *   within a per-player limit) and this website while it's open. Web push is not built (it would store push addresses).
 */

import { DEFAULT_AGENCY, FLIGHT_TYPES, stockAt, type FlightType, type RestockModel, type StockPoint as AbroadPoint } from './abroad';
import { readFavorites, type MarketFavorite } from './market-pool';

type Obj = Record<string, unknown>;
const obj = (x: unknown): Obj | null => (x && typeof x === 'object' && !Array.isArray(x) ? (x as Obj) : null);
const num = (x: unknown): number | null => (typeof x === 'number' && Number.isFinite(x) ? x : typeof x === 'string' && /^\s*-?\d+(\.\d+)?\s*$/.test(x) ? Number(x) : null);
const clamp = (x: unknown, lo: number, hi: number, dflt: number): number => {
  const n = num(x);
  return n === null ? dflt : Math.max(lo, Math.min(hi, n));
};

const MIN = 60_000;
const HOUR = 60 * MIN;

/** The lists-sync list of the player's alert rules: `{ "<rule id>": rule }` (the extension's storage key of the same name). */
export const ALERT_RULES_LIST = 'omnitorn.alertRules';
/** Rules one player may keep. */
export const ALERT_RULES_MAX = 40;
/** The server's check: every minute (ALERTS_CHECK_SECONDS changes it, 10–600 s). */
export const ALERT_CHECK_MS = MIN;
/** The same alert (one rule, one thing) again at the soonest after this, even if it went off and on in between. */
export const ALERT_REPEAT_MS = 30 * MIN;
/** Alerts a player's feed keeps (in the server's memory only), and for how long. */
export const ALERT_FEED_MAX = 50;
export const ALERT_FEED_KEEP_MS = 24 * HOUR;
/** Discord DMs one player gets for alerts at most an hour (the bot's per-player limit; one round's alerts go as one DM). */
export const ALERT_DM_PER_HOUR = 6;
/** Market listings older than this don't set off a price alert (they may be gone). */
export const ALERT_LISTING_FRESH_MS = 30 * MIN;
/** Stock abroad seen longer ago than this doesn't set off an alert. */
export const ALERT_ABROAD_FRESH_MS = 6 * HOUR;
/** A timed alert (bank, landing) goes from `before` minutes ahead until this long after the moment (the server was busy or restarting). */
export const ALERT_LATE_MS = 10 * MIN;

export type AlertKind = 'stock' | 'stockMove' | 'favorites' | 'abroad' | 'bank' | 'travel';
export const ALERT_KINDS: readonly AlertKind[] = ['stock', 'stockMove', 'favorites', 'abroad', 'bank', 'travel'];

/** Where an alert goes (each opt-in per rule). */
export interface AlertVia {
  /** The Omni-Torn extension (or Torn PDA): a notification. */
  ext: boolean;
  /** A Discord DM from the Omni-Torn bot, to the player's own linked Discord account. */
  discord: boolean;
  /** This website while it's open (a browser notification, once allowed). */
  site: boolean;
}
export type AlertChannel = keyof AlertVia;
export const ALERT_CHANNELS: readonly AlertChannel[] = ['ext', 'discord', 'site'];

interface RuleBase {
  id: string;
  on: boolean;
  via: AlertVia;
  /** When it was made or last changed (ms). */
  at: number;
}

/** A stock's price at or above / at or below a target. */
export interface StockRule extends RuleBase {
  kind: 'stock';
  stockId: number;
  above: number | null;
  below: number | null;
}
/** A stock moved at least `pct` % (up, down or either way) over the last `hours`. */
export interface StockMoveRule extends RuleBase {
  kind: 'stockMove';
  stockId: number;
  pct: number;
  hours: number;
  dir: 'up' | 'down' | 'any';
}
/** Any favorite (Market → Deals) seen at or under its target price. */
export interface FavoritesRule extends RuleBase {
  kind: 'favorites';
}
/** Stock abroad: back in stock (at least `want`), or "leave now" (leaving now lands you with at least `want` there). */
export interface AbroadRule extends RuleBase {
  kind: 'abroad';
  country: string;
  itemId: number;
  mode: 'stock' | 'leave';
  flight: FlightType;
  want: number;
}
/** Your bank investment ends (`before` minutes ahead; 0 = when it ends). */
export interface BankRule extends RuleBase {
  kind: 'bank';
  before: number;
}
/** You land (`before` minutes ahead; 0 = when you land). */
export interface TravelRule extends RuleBase {
  kind: 'travel';
  before: number;
}

export type AlertRule = StockRule | StockMoveRule | FavoritesRule | AbroadRule | BankRule | TravelRule;

const RULE_ID = /^[\w-]{1,24}$/;
const COUNTRY = /^[a-z]{3}$/;

/** A new rule's id (short, random enough within one player's list). */
export function newRuleId(now: number, rand: () => number = Math.random): string {
  return `${now.toString(36)}${Math.floor(rand() * 36 ** 4).toString(36).padStart(4, '0')}`.slice(-16);
}

function cleanVia(x: unknown): AlertVia {
  const v = obj(x);
  return { ext: v?.ext === true, discord: v?.discord === true, site: v?.site === true };
}

/** One rule as kept (anything odd: null). */
export function cleanRule(id: string, x: unknown): AlertRule | null {
  const o = obj(x);
  if (!o || !RULE_ID.test(id)) return null;
  const base = { id, on: o.on !== false, via: cleanVia(o.via), at: clamp(o.at, 0, 1e14, 0) };
  const posId = (v: unknown, max: number): number | null => {
    const n = num(v);
    return n !== null && Number.isInteger(n) && n > 0 && n <= max ? n : null;
  };
  const price = (v: unknown): number | null => {
    const n = num(v);
    return n !== null && n > 0 && n < 1e7 ? Math.round(n * 100) / 100 : null;
  };
  switch (o.kind) {
    case 'stock': {
      const stockId = posId(o.stockId, 1000);
      const above = price(o.above);
      const below = price(o.below);
      return stockId && (above !== null || below !== null) ? { ...base, kind: 'stock', stockId, above, below } : null;
    }
    case 'stockMove': {
      const stockId = posId(o.stockId, 1000);
      const dir = o.dir === 'up' || o.dir === 'down' ? o.dir : 'any';
      return stockId ? { ...base, kind: 'stockMove', stockId, pct: Math.round(clamp(o.pct, 0.1, 1000, 5) * 10) / 10, hours: Math.round(clamp(o.hours, 1, 168, 24)), dir } : null;
    }
    case 'favorites':
      return { ...base, kind: 'favorites' };
    case 'abroad': {
      const itemId = posId(o.itemId, 1e6);
      const country = typeof o.country === 'string' && COUNTRY.test(o.country) && DEFAULT_AGENCY.countries.some((c) => c.code === o.country) ? o.country : null;
      const flight = (FLIGHT_TYPES as readonly unknown[]).includes(o.flight) ? (o.flight as FlightType) : 'standard';
      return itemId && country ? { ...base, kind: 'abroad', country, itemId, mode: o.mode === 'leave' ? 'leave' : 'stock', flight, want: Math.round(clamp(o.want, 1, 1000, 1)) } : null;
    }
    case 'bank':
      return { ...base, kind: 'bank', before: Math.round(clamp(o.before, 0, 1440, 0)) };
    case 'travel':
      return { ...base, kind: 'travel', before: Math.round(clamp(o.before, 0, 120, 0)) };
  }
  return null;
}

/** The rules from the player's lists document (`{ [ALERT_RULES_LIST]: { "<id>": rule } }`), oldest first. */
export function readAlertRules(lists: unknown): AlertRule[] {
  const l = obj(obj(lists)?.[ALERT_RULES_LIST]);
  if (!l) return [];
  return Object.entries(l)
    .map(([id, v]) => cleanRule(id, v))
    .filter((r): r is AlertRule => !!r)
    .sort((a, b) => a.at - b.at || a.id.localeCompare(b.id))
    .slice(0, ALERT_RULES_MAX);
}

/** The rules as the lists document keeps them. */
export function alertRulesDoc(rules: readonly AlertRule[]): Record<string, Omit<AlertRule, 'id'>> {
  return Object.fromEntries(rules.slice(0, ALERT_RULES_MAX).map(({ id, ...rest }) => [id, rest]));
}

/** Does any rule that's on go this way? */
export const rulesUse = (rules: readonly AlertRule[], via: AlertChannel): boolean => rules.some((r) => r.on && r.via[via]);

// ---------------------------------------------------------------- what a rule says, in plain words

export const COUNTRY_NAMES: Readonly<Record<string, string>> = Object.fromEntries(DEFAULT_AGENCY.countries.map((c) => [c.code, c.name]));
const FLIGHT_NAMES: Record<FlightType, string> = { standard: 'standard flight', airstrip: 'airstrip', private: 'private island (WLT)', business: 'business class' };

const money = (n: number): string => `$${Math.round(n).toLocaleString('en-US')}`;
const stockMoney = (n: number): string => `$${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const spanText = (hours: number): string => (hours === 1 ? 'an hour' : hours === 24 ? 'a day' : hours % 24 === 0 ? `${hours / 24} days` : `${hours} hours`);
const mins = (n: number): string => (n === 0 ? '' : n % 60 === 0 ? ` ${n / 60} h before` : ` ${n} min before`);

/** "TSB at or above $1,234.00" — names come from `names` (stock acronyms, item names) when known. */
export function describeRule(r: AlertRule, names: { stock?: (id: number) => string | null; item?: (id: number) => string | null } = {}): string {
  const stock = (id: number) => names.stock?.(id) ?? `stock #${id}`;
  const item = (id: number) => names.item?.(id) ?? `item #${id}`;
  switch (r.kind) {
    case 'stock':
      return [r.above !== null ? `${stock(r.stockId)} at or above ${stockMoney(r.above)}` : '', r.below !== null ? `${stock(r.stockId)} at or below ${stockMoney(r.below)}` : ''].filter(Boolean).join(', or ');
    case 'stockMove':
      return `${stock(r.stockId)} ${r.dir === 'up' ? 'up' : r.dir === 'down' ? 'down' : 'up or down'} ${r.pct}% or more over ${spanText(r.hours)}`;
    case 'favorites':
      return 'A favorite seen at or under its target price (Market → Deals)';
    case 'abroad':
      return r.mode === 'leave'
        ? `Leave now for ${item(r.itemId)} in ${COUNTRY_NAMES[r.country] ?? r.country}: at least ${r.want} there when you land (${FLIGHT_NAMES[r.flight]})`
        : `${item(r.itemId)} in ${COUNTRY_NAMES[r.country] ?? r.country} back in stock (at least ${r.want})`;
    case 'bank':
      return `Your bank investment ends${mins(r.before)}`;
    case 'travel':
      return `You land${mins(r.before)}`;
  }
}

// ---------------------------------------------------------------- the checks

export interface AlertStock {
  acronym: string;
  name: string;
  price: number;
  /** Kept prices, oldest first (a week is plenty). */
  points: ReadonlyArray<{ at: number; price: number }>;
}

export interface AlertListing {
  price: number;
  source: 'itemmarket' | 'bazaar' | 'auction';
  sellerId: number | null;
  sellerName: string | null;
  seenAt: number;
}

export interface AlertAbroadItem {
  id: number;
  name: string | null;
  quantity: number;
  seenAt: number;
  model: RestockModel;
  last: AbroadPoint | null;
}

/** What the server has for one player's checks (everything optional: unknown = nothing goes off). */
export interface AlertData {
  now: number;
  stocks: ReadonlyMap<number, AlertStock>;
  favorites: readonly MarketFavorite[];
  /** The cheapest fresh listing of an item (Item Market or bazaar) the server knows, or null. */
  cheapest: (itemId: number) => AlertListing | null;
  itemName: (itemId: number) => string | null;
  /** Shops abroad: country → its items. */
  abroad: Readonly<Record<string, readonly AlertAbroadItem[]>>;
  /** Your bank investment's end (ms; null: none known). */
  bankUntil: number | null;
  /** Your flight: where to and when you land (null: not flying, or unknown). */
  travel: { destination: string; arrivalAt: number } | null;
}

/**
 * One thing a rule watches, right now. `key` names it (the rule and what it's about — a timed one carries its time, so
 * each investment or flight goes off once); `on`: it holds now. The server sends an alert when `on` turns true.
 */
export interface AlertState {
  ruleId: string;
  key: string;
  on: boolean;
  title: string;
  text: string;
  /** Where to go on Torn (the player clicks). */
  url: string;
}

export const TORN_URLS = {
  stocks: 'https://www.torn.com/page.php?sid=stocks',
  bank: 'https://www.torn.com/bank.php',
  home: 'https://www.torn.com/index.php',
  travel: 'https://www.torn.com/page.php?sid=travel',
  itemMarket: (id: number) => `https://www.torn.com/page.php?sid=ItemMarket#/market/view=search&itemID=${id}`,
  bazaar: (player: number, item: number) => `https://www.torn.com/bazaar.php?userId=${player}&itemId=${item}#/`,
} as const;

/** The price `hours` ago: the newest kept point at or before then (null: the history doesn't reach back that far). */
export function priceAgo(points: ReadonlyArray<{ at: number; price: number }>, now: number, hours: number): number | null {
  const t = now - hours * HOUR;
  let best: { at: number; price: number } | null = null;
  for (const p of points) if (p.at <= t && (!best || p.at > best.at)) best = p;
  // A point far older than asked (a gap in the readings) says little: at most a quarter of the window (or 30 min) off.
  if (!best || t - best.at > Math.max(30 * MIN, (hours * HOUR) / 4)) return null;
  return best.price > 0 ? best.price : null;
}

const oneWayMs = (country: string, flight: FlightType): number | null => {
  const c = DEFAULT_AGENCY.countries.find((x) => x.code === country);
  const s = c?.seconds[flight] ?? null;
  return s ? s * 1000 : null;
};

const clock = (ms: number): string => new Date(ms).toISOString().slice(11, 16);

/** Every rule that's on, checked against what the server has. Pure. */
export function checkRules(rules: readonly AlertRule[], d: AlertData): AlertState[] {
  const out: AlertState[] = [];
  for (const r of rules) {
    if (!r.on) continue;
    switch (r.kind) {
      case 'stock': {
        const s = d.stocks.get(r.stockId);
        if (!s) break;
        if (r.above !== null) out.push({ ruleId: r.id, key: `${r.id}:above`, on: s.price >= r.above, title: `${s.acronym} at ${stockMoney(s.price)}`, text: `${s.name} (${s.acronym}) is at ${stockMoney(s.price)} — at or above your ${stockMoney(r.above)}.`, url: TORN_URLS.stocks });
        if (r.below !== null) out.push({ ruleId: r.id, key: `${r.id}:below`, on: s.price <= r.below, title: `${s.acronym} at ${stockMoney(s.price)}`, text: `${s.name} (${s.acronym}) is at ${stockMoney(s.price)} — at or below your ${stockMoney(r.below)}.`, url: TORN_URLS.stocks });
        break;
      }
      case 'stockMove': {
        const s = d.stocks.get(r.stockId);
        if (!s) break;
        const then = priceAgo(s.points, d.now, r.hours);
        if (then === null) break;
        const pct = ((s.price - then) / then) * 100;
        const span = spanText(r.hours);
        for (const dir of r.dir === 'any' ? (['up', 'down'] as const) : [r.dir]) {
          const on = dir === 'up' ? pct >= r.pct : pct <= -r.pct;
          out.push({ ruleId: r.id, key: `${r.id}:${dir}`, on, title: `${s.acronym} ${pct >= 0 ? 'up' : 'down'} ${Math.abs(pct).toFixed(1)}%`, text: `${s.name} (${s.acronym}) went from ${stockMoney(then)} to ${stockMoney(s.price)} over ${span} (${pct >= 0 ? '+' : '−'}${Math.abs(pct).toFixed(1)}%).`, url: TORN_URLS.stocks });
        }
        break;
      }
      case 'favorites': {
        for (const f of d.favorites) {
          if (f.target <= 0) continue;
          const l = d.cheapest(f.itemId);
          const fresh = !!l && l.source !== 'auction' && d.now - l.seenAt <= ALERT_LISTING_FRESH_MS;
          const name = d.itemName(f.itemId) ?? `Item #${f.itemId}`;
          const where = l?.source === 'bazaar' ? `${l.sellerName ?? (l.sellerId ? `#${l.sellerId}` : 'a')}’s bazaar` : 'the Item Market';
          out.push({
            ruleId: r.id,
            key: `${r.id}:fav:${f.itemId}`,
            on: fresh && l!.price <= f.target,
            title: `${name} at ${l ? money(l.price) : '—'}`,
            text: l ? `${name} at ${money(l.price)} in ${where} — your target is ${money(f.target)}.` : '',
            url: l?.source === 'bazaar' && l.sellerId ? TORN_URLS.bazaar(l.sellerId, f.itemId) : TORN_URLS.itemMarket(f.itemId),
          });
        }
        break;
      }
      case 'abroad': {
        const it = d.abroad[r.country]?.find((x) => x.id === r.itemId);
        const country = COUNTRY_NAMES[r.country] ?? r.country;
        if (!it) break;
        const name = it.name ?? d.itemName(r.itemId) ?? `Item #${r.itemId}`;
        const fresh = d.now - it.seenAt <= ALERT_ABROAD_FRESH_MS;
        if (r.mode === 'stock') {
          out.push({ ruleId: r.id, key: `${r.id}:stock`, on: fresh && it.quantity >= r.want, title: `${name} in stock in ${country}`, text: `${country} has ${it.quantity.toLocaleString('en-US')} × ${name} (seen ${Math.max(0, Math.round((d.now - it.seenAt) / MIN))} min ago).`, url: TORN_URLS.travel });
        } else {
          const way = oneWayMs(r.country, r.flight);
          const land = way ? d.now + way : null;
          const there = land && fresh ? stockAt(it.model, it.last, land) : null;
          out.push({ ruleId: r.id, key: `${r.id}:leave`, on: there !== null && there >= r.want, title: `Leave now for ${name}`, text: `Leave now for ${country} (${FLIGHT_NAMES[r.flight]}): you’d land at ${land ? clock(land) : '—'} TCT with about ${there ?? 0} × ${name} there.`, url: TORN_URLS.travel });
        }
        break;
      }
      case 'bank': {
        const t = d.bankUntil;
        if (!t) break;
        const due = t - r.before * MIN;
        out.push({ ruleId: r.id, key: `${r.id}:bank:${Math.round(t / MIN)}`, on: d.now >= due && d.now < t + ALERT_LATE_MS, title: d.now < t ? `Bank investment ends in ${Math.ceil((t - d.now) / MIN)} min` : 'Bank investment ended', text: `Your bank investment ${d.now >= t ? 'has ended' : `ends at ${clock(t)} TCT`} — collect it and invest again.`, url: TORN_URLS.bank });
        break;
      }
      case 'travel': {
        const f = d.travel;
        if (!f) break;
        const due = f.arrivalAt - r.before * MIN;
        out.push({ ruleId: r.id, key: `${r.id}:land:${Math.round(f.arrivalAt / MIN)}`, on: d.now >= due && d.now < f.arrivalAt + ALERT_LATE_MS, title: d.now < f.arrivalAt ? `Landing in ${Math.ceil((f.arrivalAt - d.now) / MIN)} min` : 'Landed', text: `${d.now >= f.arrivalAt ? 'You landed' : `You land at ${clock(f.arrivalAt)} TCT`} in ${f.destination}.`, url: TORN_URLS.home });
        break;
      }
    }
  }
  return out;
}

// ---------------------------------------------------------------- the feed (what went off)

/** One alert that went off (the server keeps a player's last ALERT_FEED_MAX for a day, in memory). */
export interface AlertFired {
  id: string;
  ruleId: string;
  kind: AlertKind | 'test';
  title: string;
  text: string;
  url: string;
  at: number;
  via: AlertVia;
  /** What Discord said: sent, not linked, over the limit, DMs closed… (null: not asked). */
  discord: 'sent' | 'not-linked' | 'limit' | 'failed' | 'terms' | 'closed' | null;
  /** A pooled kind held for a player below terms 17 (POOLED_ALERT_KINDS): shown only on the website's Alerts page while it's open. */
  pageOnly?: boolean;
}

/** `GET /api/me/alerts?since=`: what went off since then, newest first, and what the checks can see for you. */
export interface AlertsAnswer {
  alerts: AlertFired[];
  at: number;
  /** Stock abroad and favorites go to the background channels too (the player is on terms 17 — ALERTS_POOLED_TERMS). */
  pooledInBackground?: boolean;
  checkEveryMs: number;
  /** The newest stock prices the server has (ms; null: none). */
  stocksAt: number | null;
  /** Your bank investment's end as background tracking last read it (null: unknown — needs tracking, history and a Limited key). */
  bankUntil: number | null;
  /** Your flight as your faction share last said (null: not flying or unknown). */
  travel: { destination: string; arrivalAt: number } | null;
}

/** The alerts after `since` that go this way, oldest first (what a device shows). */
export function alertsFor(feed: readonly AlertFired[], via: AlertChannel, since: number): AlertFired[] {
  return feed.filter((a) => a.at > since && a.via[via]).sort((a, b) => a.at - b.at);
}

/** One DM for a round's alerts (Discord's 2,000-character limit kept). */
export function alertDmText(list: ReadonlyArray<Pick<AlertFired, 'title' | 'text' | 'url'>>): string {
  const lines = list.slice(0, 10).map((a) => `🔔 **${a.title}** — ${a.text}${a.url ? ` <${a.url}>` : ''}`);
  if (list.length > 10) lines.push(`…and ${list.length - 10} more (the website’s Alerts page lists them).`);
  return `${lines.join('\n')}\n-# Omni-Torn alerts — change them on the website (Omni-Torn → Alerts).`.slice(0, 1990);
}

/** Alert DMs from the Discord bot (and the alerts' terms wording) need the player's recorded terms version 16. */
export const ALERTS_DM_TERMS = 16;
/**
 * Alerts at all need terms 16 (the fact-check, 0.136.0): below it the server doesn't check a player's rules or keep what
 * went off, and their history snapshots don't keep the bank investment's end.
 */
export const ALERTS_TERMS = 16;

/**
 * Kinds whose alerts come from pools of what other players saw on Torn's pages (the market pool, stock abroad). The owner's
 * call 2026-11-02 ("We arent using websocket exploits to view non viewed pages or anything. Just notifying what other
 * people saw."): from terms version ALERTS_POOLED_TERMS (17) they go wherever the player chose, like every other kind.
 * Below 17 they stay as before (the 0.136.0 fact-check): only on the website's Alerts page while it's open — no extension
 * notification, no Discord DM, no browser notification. Stocks, bank and landing come from API data and go everywhere chosen.
 */
export const POOLED_ALERT_KINDS: readonly AlertKind[] = ['favorites', 'abroad'];
export const ALERTS_POOLED_TERMS = 17;
export const isPooledKind = (k: AlertKind | 'test'): boolean => (POOLED_ALERT_KINDS as readonly string[]).includes(k);
/** Is this kind held to the Alerts page for a player on this terms version (a pooled kind below 17)? */
export const pooledHeld = (kind: AlertKind | 'test', terms: number): boolean => isPooledKind(kind) && terms < ALERTS_POOLED_TERMS;
/** Where an alert of this kind may go: a pooled kind below terms 17 only to the Alerts page (none of the background channels). */
export const deliveryVia = (kind: AlertKind | 'test', via: AlertVia, terms = 0): AlertVia => (pooledHeld(kind, terms) ? { ext: false, discord: false, site: false } : { ...via });
/** What the rule form says for a pooled kind below terms 17. */
export const POOLED_HOLD_TEXT = `For now on this page while it’s open — the places you tick are kept and used once you agree to terms version ${ALERTS_POOLED_TERMS}.`;
